//# sourceMappingURL=timestamp_.mjs.map
/// <reference types="./timestamp_.d.mts" />
import * as $json from "../../../gleam_json/gleam/json.mjs";
import * as $int from "../../../gleam_stdlib/gleam/int.mjs";
import * as $list from "../../../gleam_stdlib/gleam/list.mjs";
import * as $pair from "../../../gleam_stdlib/gleam/pair.mjs";
import * as $result from "../../../gleam_stdlib/gleam/result.mjs";
import * as $string from "../../../gleam_stdlib/gleam/string.mjs";
import * as $calendar from "../../../gleam_time/gleam/time/calendar.mjs";
import * as $timestamp from "../../../gleam_time/gleam/time/timestamp.mjs";
import { Error, toList, Empty as $Empty } from "../../gleam.mjs";
import * as $month from "../../gleam/time/calendar/month.mjs";
import { formatDate as format_date } from "./timestamp_.ffi.mjs";

/**
 * Convert a `timestamp.Timestamp` to `"YYYY-MM-DD"` format. `"-"` is used as
 * separator in the example, but it can be chosen freely.
 *
 * ```gleam
 * to_date_yyyymmdd(Timestamp(seconds: 1748642400, nanoseconds: 0), "-")
 * //  -> "2025-05-31"
 * ```
 */
export function to_date_yyyymmdd(time, separator) {
  let $ = $timestamp.to_calendar(time, $calendar.local_offset());
  let date = $[0];
  let month = $month.to_int(date.month);
  let month$1 = $string.pad_start($int.to_string(month), 2, "0");
  let year = $string.pad_start($int.to_string(date.year), 4, "0");
  let day = $string.pad_start($int.to_string(date.day), 2, "0");
  return $string.join(toList([year, month$1, day]), separator);
}

/**
 * Parse a time from an `<input type="date">` into a `timestamp.Timestamp` data,
 * keeping only the day, discarding the other parts.
 */
export function from_time_input(value) {
  let $ = $string.split(value, "-");
  if ($ instanceof $Empty) {
    return new Error(undefined);
  } else {
    let $1 = $.tail;
    if ($1 instanceof $Empty) {
      return new Error(undefined);
    } else {
      let $2 = $1.tail;
      if ($2 instanceof $Empty) {
        return new Error(undefined);
      } else {
        let $3 = $2.tail;
        if ($3 instanceof $Empty) {
          let year = $.head;
          let month = $1.head;
          let day = $2.head;
          return $result.try$(
            $int.parse(year),
            (year) => {
              return $result.try$(
                $month.from_input(month),
                (month) => {
                  return $result.map(
                    $int.parse(day),
                    (day) => {
                      let date = new $calendar.Date(year, month, day);
                      let time = new $calendar.TimeOfDay(12, 0, 0, 0);
                      return $timestamp.from_calendar(
                        date,
                        time,
                        $calendar.local_offset(),
                      );
                    },
                  );
                },
              );
            },
          );
        } else {
          return new Error(undefined);
        }
      }
    }
  }
}

/**
 * Parse a time from an `<input type="datetime-local">` into a
 * `timestamp.Timestamp` data.
 */
export function from_datetime_local(value) {
  let $ = $string.split(value, "T");
  if ($ instanceof $Empty) {
    return new Error(undefined);
  } else {
    let $1 = $.tail;
    if ($1 instanceof $Empty) {
      return new Error(undefined);
    } else {
      let $2 = $1.tail;
      if ($2 instanceof $Empty) {
        let date = $.head;
        let time = $1.head;
        let $3 = $string.split(date, "-");
        let $4 = $string.split(time, ":");
        if ($3 instanceof $Empty) {
          return new Error(undefined);
        } else if ($4 instanceof $Empty) {
          return new Error(undefined);
        } else {
          let $5 = $3.tail;
          if ($5 instanceof $Empty) {
            return new Error(undefined);
          } else {
            let $6 = $4.tail;
            if ($6 instanceof $Empty) {
              return new Error(undefined);
            } else {
              let $7 = $5.tail;
              if ($7 instanceof $Empty) {
                return new Error(undefined);
              } else {
                let $8 = $6.tail;
                if ($8 instanceof $Empty) {
                  return new Error(undefined);
                } else {
                  let $9 = $7.tail;
                  if ($9 instanceof $Empty) {
                    let $10 = $8.tail;
                    if ($10 instanceof $Empty) {
                      let year = $3.head;
                      let hours = $4.head;
                      let month = $5.head;
                      let minutes = $6.head;
                      let day = $7.head;
                      let seconds = $8.head;
                      return $result.try$(
                        $int.parse(year),
                        (year) => {
                          return $result.try$(
                            $month.from_input(month),
                            (month) => {
                              return $result.try$(
                                $int.parse(day),
                                (day) => {
                                  let date$1 = new $calendar.Date(
                                    year,
                                    month,
                                    day,
                                  );
                                  return $result.try$(
                                    $int.parse(hours),
                                    (hours) => {
                                      return $result.try$(
                                        $int.parse(minutes),
                                        (minutes) => {
                                          return $result.map(
                                            $int.parse(seconds),
                                            (seconds) => {
                                              let time$1 = new $calendar.TimeOfDay(
                                                hours,
                                                minutes,
                                                seconds,
                                                0,
                                              );
                                              return $timestamp.from_calendar(
                                                date$1,
                                                time$1,
                                                $calendar.local_offset(),
                                              );
                                            },
                                          );
                                        },
                                      );
                                    },
                                  );
                                },
                              );
                            },
                          );
                        },
                      );
                    } else {
                      return new Error(undefined);
                    }
                  } else {
                    return new Error(undefined);
                  }
                }
              }
            }
          }
        }
      } else {
        return new Error(undefined);
      }
    }
  }
}

/**
 * Convert a `timestamp.Timestamp` to `YYYY-MM-DDTHH-MM-SS` format. To use
 * with `<input type="datetime-local">`.
 */
export function to_datetime_local(value) {
  let $ = $timestamp.to_calendar(value, $calendar.local_offset());
  let date = $[0];
  let time = $[1];
  let month = $month.to_int(date.month);
  let _block;
  let _pipe = toList([date.year, month, date.day]);
  let _pipe$1 = $list.map(_pipe, $int.to_string);
  let _pipe$2 = $list.map(
    _pipe$1,
    (_capture) => { return $string.pad_start(_capture, 2, "0"); },
  );
  _block = $string.join(_pipe$2, "-");
  let date$1 = _block;
  let _block$1;
  let _pipe$3 = toList([time.hours, time.minutes, time.seconds]);
  let _pipe$4 = $list.map(_pipe$3, $int.to_string);
  let _pipe$5 = $list.map(
    _pipe$4,
    (_capture) => { return $string.pad_start(_capture, 2, "0"); },
  );
  _block$1 = $string.join(_pipe$5, ":");
  let time$1 = _block$1;
  return $string.join(toList([date$1, time$1]), "T");
}

/**
 * Turn a `Timestamp` into a UTC RFC-3339.
 * The conversion will automatically read `navigator.language`
 * to determine the locale to translate in.
 *
 * ```gleam
 * let ts = timestamp.system_time()
 * to_locale_full_date(ts)
 * // -> with "fr-FR" locale: "31/05/2025"
 * // -> with "en-US" locale: "5/31/2025"
 * ```
 */
export function to_locale_full_date(time) {
  let _pipe = time;
  let _pipe$1 = $timestamp.to_rfc3339(_pipe, $calendar.utc_offset);
  return format_date(_pipe$1);
}

export function encode(timestamp) {
  let _pipe = timestamp;
  let _pipe$1 = $timestamp.to_rfc3339(_pipe, $calendar.utc_offset);
  return $json.string(_pipe$1);
}

export function to_seconds(timestamp) {
  let _pipe = timestamp;
  let _pipe$1 = $timestamp.to_unix_seconds_and_nanoseconds(_pipe);
  return $pair.first(_pipe$1);
}

export function to_unix_milliseconds(timestamp) {
  let $ = $timestamp.to_unix_seconds_and_nanoseconds(timestamp);
  let seconds = $[0];
  let nanos = $[1];
  let millis = globalThis.Math.trunc(nanos / 1_000_000);
  let seconds$1 = seconds * 1000;
  return seconds$1 + millis;
}

/**
 * Returns the current timestamp, in seconds.
 */
export function now() {
  let _pipe = $timestamp.system_time();
  return to_seconds(_pipe);
}

export function to_utc_calendar(timestamp) {
  return $timestamp.to_calendar(timestamp, $calendar.utc_offset);
}

export function from_utc_calendar(cal) {
  let date = cal[0];
  let time_of_day = cal[1];
  return $timestamp.from_calendar(date, time_of_day, $calendar.utc_offset);
}
